import { createSlice } from '@reduxjs/toolkit';
import { updateOrder } from '../utils/order/orderUtils';

const afterOrderItemsChange = (state) => {
	return updateOrder(state);
};

const initialState = localStorage.getItem('order')
	? JSON.parse(localStorage.getItem('order'))
	: {
			orderItems: [],
			orderId: '',
			contractNumbers: [],
			invoiceNumbers: [],
			customer: {},
			sale: {},
			assistant: {},
			deliveryAddress: {},
			paymentMethod: '全额支付',
			paymentDetail: {},
			scheduleNodes: [],
			currentOrderOId: '',
			orderNotes: [],
		};

const orderSlice = createSlice({
	name: 'order',
	initialState,
	reducers: {
		setCurrentOrderOId: (state, action) => {
			state.currentOrderOId = action.payload;
			return updateOrder(state);
		},

		clearCurrentOrderOId: (state, action) => {
			state.currentOrderOId = '';
			return updateOrder(state);
		},

		addToOrder: (state, action) => {
			const item = action.payload;
			state.orderItems = Array.from(new Set(state.orderItems.concat(item)));
			return afterOrderItemsChange(state);
		},

		addDraftItemsToOrder: (state, action) => {
			state.orderItems = action.payload;
			return afterOrderItemsChange(state);
		},

		updateOrderItems: (state, action) => {
			state.orderItems = state.orderItems.map((x) => (x._id === action.payload._id ? action.payload : x));
			return afterOrderItemsChange(state);
		},

		updateOrderItemsSellingPrice: (state, action) => {
			const { excludeFromPerformanceMatch, sellingPriceBeforeComplimentary, ...rest } = action.payload ?? {};
			const price = Number(rest.sellingPrice);
			const normalized = {
				...rest,
				sellingPrice: Number.isFinite(price) ? Math.max(0, price) : 0,
				isDiscount: Number.isFinite(price) && price > 0 && price < Number(rest.minimumSellingPrice),
			};
			state.orderItems = state.orderItems.map((x) => (x._id === normalized._id ? normalized : x));
			return afterOrderItemsChange(state);
		},

		updateOrderItemsQty: (state, action) => {
			state.orderItems = state.orderItems.map((x) => (x._id === action.payload._id ? action.payload : x));
			return afterOrderItemsChange(state);
		},

		saveOrderId: (state, action) => {
			state.orderId = action.payload;
			return updateOrder(state);
		},

		saveOrderIndexOfDay: (state, action) => {
			state.orderIndexOfDay = action.payload;
			return updateOrder(state);
		},

		saveCustomer: (state, action) => {
			state.customer = action.payload;
			return updateOrder(state);
		},

		saveSale: (state, action) => {
			state.sale = action.payload;
			return updateOrder(state);
		},

		saveAssistant: (state, action) => {
			state.assistant = action.payload;
			return updateOrder(state);
		},

		savePaymentMethod: (state, action) => {
			state.paymentMethod = action.payload;
			return updateOrder(state);
		},

		setPartialPaymentOption: (state, action) => {
			state.paymentDetail.prePaymentOption = action.payload;
			return updateOrder(state);
		},

		savePaymentDetail: (state, action) => {
			state.paymentDetail.prePaymentOption = action.payload.prePaymentOption;
			state.paymentDetail.prePaymentPercent = action.payload.prePaymentPercent;
			state.paymentDetail.percentPayment = action.payload.percentPayment;
			state.paymentDetail.percentBalance = action.payload.percentBalance;
			state.paymentDetail.fixedPayment = action.payload.fixedPayment;
			state.paymentDetail.fixedBalance = action.payload.fixedBalance;
			state.paymentDetail.paymentExplain = action.payload.paymentExplain;
			state.paymentDetail.arrivalTime = action.payload.arrivalTime;
			state.paymentDetail.warrantyPeriod = action.payload.warrantyPeriod;
			state.paymentDetail.orderNotes = action.payload.orderNotes;
			return updateOrder(state);
		},

		saveScheduleNodes: (state, action) => {
			state.scheduleNodes = action.payload;
			return updateOrder(state);
		},

		addToScheduleNodes: (state, action) => {
			const item = action.payload;
			state.scheduleNodes = Array.from(new Set(state.scheduleNodes.concat(item)));
			return updateOrder(state);
		},
		moveUpScheduleNode: (state, action) => {
			const { fromIndex, toIndex } = action.payload;
			state.scheduleNodes.splice(toIndex, 0, state.scheduleNodes.splice(fromIndex, 1)[0]);
			return updateOrder(state);
		},
		moveDownScheduleNode: (state, action) => {
			const { fromIndex, toIndex } = action.payload;
			state.scheduleNodes.splice(toIndex, 0, state.scheduleNodes.splice(fromIndex, 1)[0]);
			return updateOrder(state);
		},
		saveContractNumbers: (state, action) => {
			state.contractNumbers = Array.isArray(action.payload) ? action.payload : [];
			return updateOrder(state);
		},
		saveInvoiceNumbers: (state, action) => {
			state.invoiceNumbers = Array.isArray(action.payload) ? action.payload : [];
			return updateOrder(state);
		},
		removeScheduleNode: (state, action) => {
			state.scheduleNodes.splice(action.payload, 1);

			return updateOrder(state);
		},

		clearScheduleNodes: (state, action) => {
			state.scheduleNodes = [];
			return updateOrder(state);
		},

		addScheduleNodesToOrder: (state, action) => {
			const node = action.payload;
			state.scheduleNodes = Array.from(new Set(state.scheduleNodes.concat(node)));
			return updateOrder(state);
		},

		addScheduleNodeToOrder: (state, action) => {
			state.scheduleNodes = state.scheduleNodes.concat(action.payload);
			return updateOrder(state);
		},

		removeScheduleNodesFromOrder: (state, action) => {
			state.scheduleNodes = state.scheduleNodes.filter((x) => x.title !== action.payload);
			return updateOrder(state);
		},

		removeFromOrder: (state, action) => {
			const ids = Array.isArray(action.payload) ? action.payload : [action.payload];
			if (!ids.length) return state;
			const idSet = new Set(ids);
			state.orderItems = state.orderItems.filter((x) => !idSet.has(x._id));
			return afterOrderItemsChange(state);
		},

		reorderOrderItems: (state, action) => {
			state.orderItems = action.payload;
			return afterOrderItemsChange(state);
		},

		clearOrderItems: (state, action) => {
			state.orderItems = [];
			localStorage.setItem('order', JSON.stringify(state));
		},

		clearCustomer: (state, action) => {
			state.customer = {};
			return updateOrder(state);
		},

		clearSale: (state, action) => {
			state.sale = {};
			state.orderId = '';
			return updateOrder(state);
		},

		clearAssistant: (state, action) => {
			state.assistant = {};
			return updateOrder(state);
		},

		resetOrder: (state) => {
			state.customer = {};
			state.sale = {};
			state.assistant = {};
			state.orderItems = [];
			state.currentOrderOId = '';
			state.orderId = '';
			state.contractNumbers = [];
			state.invoiceNumbers = [];
			state.itemsPrice = '';
			state.deliveryAddress = '';
			state.paymentMethod = '全额支付';
			state.paymentDetail = {};
			state.scheduleNodes = [];
			localStorage.setItem('order', JSON.stringify(state));
		},
	},
});

export const {
	setCurrentOrderOId,
	clearCurrentOrderOId,
	addToOrder,
	addDraftItemsToOrder,
	updateOrderItems,
	updateOrderItemsQty,
	updateOrderItemsSellingPrice,
	saveCustomer,
	saveSale,
	saveAssistant,
	clearOrderItems,
	clearCustomer,
	clearSale,
	clearAssistant,
	savePaymentMethod,
	setPartialPaymentOption,
	savePaymentDetail,
	saveScheduleNodes,
	addToScheduleNodes,
	saveContractNumbers,
	saveInvoiceNumbers,
	removeScheduleNode,
	moveUpScheduleNode,
	moveDownScheduleNode,
	clearScheduleNodes,
	addScheduleNodesToOrder,
	addScheduleNodeToOrder,
	removeScheduleNodesFromOrder,
	removeFromOrder,
	reorderOrderItems,
	resetOrder,
	saveOrderId,
	saveOrderIndexOfDay,
} = orderSlice.actions;

export default orderSlice.reducer;
